import { useEmployeeEditingCtx } from '@/app/(app)/settings/employees/[id]/provider';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Location = ApiSchemas['LocationUpsert'];

export function EmployeeLocations() {
  const { employee, update } = useEmployeeEditingCtx();

  const newLocation = (): Location => {
    return {
      office_id: employee.office_id,
      line1: '',
      line2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      latitude: null,
      longitude: null,
    };
  };

  return (
    <div className='flex w-full flex-col gap-14'>
      <LocationCard
        title='Start Location'
        location={employee.start_location ?? null}
        update={(v) => update({ start_location: v })}
        add={() => update({ start_location: newLocation() })}
        remove={() => update({ start_location: null })}
      />
      <LocationCard
        title='End Location'
        location={employee.end_location ?? null}
        update={(v) => update({ end_location: v })}
        add={() => update({ end_location: newLocation() })}
        remove={() => update({ end_location: null })}
      />
    </div>
  );
}

interface LocationCardProps {
  title: string;
  location: Location | null;
  update: (location: Location) => void;
  add: () => void;
  remove: () => void;
}

function LocationCard({ title, location, update: update, add, remove }: LocationCardProps) {
  const onChange = (v: Partial<Location>) => {
    if (!location) return;
    update({ ...location, ...v });
  };

  if (!location) {
    return (
      <div className='flex flex-col gap-2'>
        <div className='flex items-center justify-between'>
          <h2 className='text-heading-sm'>{title}</h2>
          <Button variant='outline' size='sm' onClick={add}>
            Add Location
          </Button>
        </div>
        <div className='flex h-24 w-full items-center justify-center rounded-xl border border-dashed text-sm text-muted-foreground'>
          No location set
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between'>
        <h2 className='text-heading-sm'>{title}</h2>
        <Button variant='ghost' size='sm' onClick={remove}>
          Remove Location
        </Button>
      </div>
      <div className='grid grid-cols-2 gap-x-2 gap-y-3'>
        <div className='space-y-2'>
          <Label htmlFor='line1'>Line 1</Label>
          <Input id='line1' value={location.line1} onChange={(e) => onChange({ line1: e.target.value })} />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='line2'>Line 2</Label>
          <Input id='line2' value={location.line2 ?? ''} onChange={(e) => onChange({ line2: e.target.value })} />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='city'>City</Label>
          <Input id='city' value={location.city} onChange={(e) => onChange({ city: e.target.value })} />
        </div>
        <div className='grid grid-cols-2 gap-x-2'>
          <div className='space-y-2'>
            <Label htmlFor='state'>State</Label>
            <Input id='state' value={location.state} onChange={(e) => onChange({ state: e.target.value })} />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='zip'>Zip</Label>
            <Input id='zip' value={location.zip} onChange={(e) => onChange({ zip: e.target.value })} />
          </div>
        </div>
      </div>
      <div className='mt-4 grid grid-cols-2 gap-x-2 gap-y-3'>
        <div className='space-y-2'>
          <Label htmlFor='latitude'>Latitude</Label>
          <Input
            id='latitude'
            value={location.latitude ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              if (!value || isNaN(Number(value))) {
                return;
              }
              onChange({ latitude: Number(value) });
            }}
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='longitude'>Longitude</Label>
          <Input
            id='longitude'
            value={location.longitude ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              if (!value || isNaN(Number(value))) {
                return;
              }
              onChange({ longitude: Number(value) });
            }}
          />
        </div>
      </div>
    </div>
  );
}
